import React, { Suspense, useMemo } from 'react';
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Checkbox,
  InputLabel,
  FormHelperText,
  IconButton,
  Box,
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useCustomAutocomplete } from './useCustomAutoComplete';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

interface Option {
  [key: string]: any;
}

const keepEveryOption = (): boolean => true;

interface CustomAutocompleteProps
  extends Omit<React.ComponentProps<typeof Autocomplete>, 'renderInput' | 'options'> {
  onSelect: (value: Option | Option[] | string | string[] | null) => void;
  defaultData?: Record<string, unknown>;
  label: string;
  fieldLabel?: string;
  placeholder?: string;
  labelKey?: string;
  mainKey?: string;
  renderCustomOption?: (option: Option, selected: boolean) => React.ReactNode;
  customRenderInput?: (params: any) => React.ReactNode;
  readonly?: boolean;
  error?: boolean;
  errorText?: string;
  value?: Option | Option[] | string | string[] | number | boolean | null;
  multiple?: boolean;
  fetchOptions?: (queryValue: string, defaultData?: Record<string, unknown>) => Promise<Option[]>;
  queryKey?: string;
  preSelectedOptions?: Option[];
  defaultOptions?: Option[] | string[];
  icon?: React.ElementType;
  onIconClick?: () => void;
  isDropdownMode?: boolean;
  returnStringValue?: boolean;
  filterOptions?: () => void;
  isFetchingDisabled?: boolean;
  canOffer?: (option: Option) => boolean;
}

const CustomAsyncAutocomplete: React.FC<CustomAutocompleteProps> = ({
  onSelect,
  defaultData = {},
  label,
  fieldLabel,
  placeholder,
  labelKey,
  mainKey,
  renderCustomOption,
  customRenderInput,
  readonly = false,
  error = false,
  errorText = '',
  value,
  fetchOptions,
  preSelectedOptions,
  defaultOptions = [],
  queryKey,
  icon: Icon,
  onIconClick,
  multiple = false,
  isDropdownMode = false,
  returnStringValue,
  filterOptions,
  isFetchingDisabled,
  canOffer = keepEveryOption,
  ...props
}) => {
  const { options, isLoading, isPending, handleInputChange, onOpen, onClose, refetch } =
    useCustomAutocomplete({ queryKey, defaultData, fetchOptions, isDropdownMode, isFetchingDisabled });

  const allOptions = useMemo(() => {
    const validDefaults = (defaultOptions || []).filter((o: Option | string) => o != null);
    const validOptions = (options || []).filter((o: Option) => o != null && canOffer(o));
    const map = new Map<string | number, Option | string>();
    
    validDefaults.forEach(o => {
      const key = typeof o === 'object' && mainKey ? o[mainKey] : o;
      map.set(String(key), o);
    });
    
    validOptions.forEach(o => {
      const key = typeof o === 'object' && mainKey ? o[mainKey] : o;
      if (!map.has(String(key))) map.set(String(key), o);
    });
    
    return Array.from(map.values());
  }, [options, defaultOptions, mainKey, canOffer]);

  const getOptionKey = (option: Option | string): string => {
    if (typeof option === 'string') return option;
    if (mainKey && option[mainKey]) return String(option[mainKey]);
    if (labelKey && option[labelKey]) return String(option[labelKey]);
    return JSON.stringify(option);
  };

  const internalHandleSelect = (
    _e: React.SyntheticEvent,
    newValue: Option | Option[] | string | string[] | null
  ) => {
    if (returnStringValue && mainKey) {
      if (multiple) {
        const transformed = Array.isArray(newValue)
          ? newValue.map(item => (
              typeof item === 'string' ? item : item[mainKey]
            ))
          : [];
        onSelect(transformed);
      } else {
        const transformed = newValue
          ? typeof newValue === 'string'
            ? newValue
            : newValue[mainKey]
          : null;
        onSelect(transformed);
      }
    } else {
      onSelect(newValue as Option | Option[] | null);
    }
  };

  const resolvedValue = useMemo(() => {
    if (returnStringValue && mainKey) {
      if (multiple) {
        if (Array.isArray(value)) {
          return value.map(val => {
            if (typeof val === 'string') {
              return allOptions.find(opt => 
                typeof opt !== 'string' && opt[mainKey] === val
              ) || val;
            }
            return val;
          });
        }
        return [];
      }
      if (typeof value === 'string') {
        return allOptions.find(opt => 
          typeof opt !== 'string' && opt[mainKey] === value
        ) || value;
      }
    }
    return value as Option | Option[] | null;
  }, [value, allOptions, returnStringValue, mainKey, multiple]);

  const renderAutocomplete = () => (
    <Autocomplete
      {...props}
      loading={isLoading}
      value={resolvedValue}
      options={allOptions}
      onClose={onClose}
      onChange={internalHandleSelect}
      onOpen={onOpen}
      multiple={multiple}
      {...(filterOptions !== undefined && { filterOptions: filterOptions })}
      getOptionLabel={(option: Option | string) => {
        if (typeof option === 'string') return option;
        return labelKey ? String(option?.[labelKey]) : JSON.stringify(option);
      }}
      isOptionEqualToValue={(option, value) => {
        if (mainKey) {
          const optionKey = typeof option === 'object' ? option[mainKey] : option;
          const valueKey = typeof value === 'object' ? value[mainKey] : value;
          return optionKey === valueKey;
        }
        return option === value;
      }}
      renderOption={(props, option, { selected }) => (
        <Box
          component="li"
          {...props}
          key={getOptionKey(option)}
          sx={{ opacity: isPending ? 0.5 : 1, transition: 'opacity 0.2s' }}
        >
          {multiple && (
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={selected}
            />
          )}
          {renderCustomOption ? renderCustomOption(option, selected) : (
            typeof option === 'object' && labelKey ? option[labelKey] : String(option)
          )}
        </Box>
      )}
      renderInput={params =>
        customRenderInput ? (
          customRenderInput(params)
        ) : (
          <TextField
            {...params}
            id="custom-autocomplete-input"
            name="custom-autocomplete-input"
            label={label}
            fullWidth
            placeholder={placeholder}
            onChange={e => !isDropdownMode && handleInputChange(e.target.value)}
            onFocus={() => refetch()}
            error={error}
            InputProps={{
              ...params.InputProps,
              readOnly: readonly || isDropdownMode,
              endAdornment: (
                <>
                  {isLoading && fetchOptions && <CircularProgress color="primary" size={20} />}
                  {Icon && (
                    <IconButton
                      onClick={e => {
                        e.stopPropagation();
                        onIconClick?.();
                      }}
                      size="small"
                      sx={{ marginLeft: '4px' }}
                    >
                      <Icon fontSize="medium" />
                    </IconButton>
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            inputProps={{ ...params.inputProps, readOnly: readonly || isDropdownMode }}
          />
        )
      }
    />
  );

  return (
    <>
      <Suspense fallback={<CircularProgress />}>
        {fieldLabel && (
          <InputLabel shrink htmlFor="autocomplete-input">
            {fieldLabel}
          </InputLabel>
        )}
        {renderAutocomplete()}
        {error && errorText && <FormHelperText error>{errorText}</FormHelperText>}
      </Suspense>
    </>
  );
};

export default CustomAsyncAutocomplete;